import { defineStore } from '/src/core/store';
import {
	formatFace,
} from '/src/utils/faces.js';
import { serializeFaces, deserializeFaces, toFaceEntry } from '/src/utils/faceEntries.js';
import { useEmployeeStore } from '/src/store/stores/useEmployeeStore.js';

// A different version wipes and rebuilds every face on each affected kiosk, which
// big companies may never finish (docs/optimization/post-mortem-869f22h5k.md).
// Bump it only when the Human model or the config that generates the local
// embeddings changes.
export const FACE_DATA_VERSION = '2025-11-10-v1';

// Marker of the server side collection: a different version only asks Amazon
// kiosks for a /reindex (see useFacesService), it costs nothing on the device.
// /reindex only covers the companies linked at request time, so the marker is
// paired with serverReindexCompanyIds: linking a company re-asks for it.
export const SERVER_REINDEX_VERSION = '2026-10-07-v1';

export const useFaceStore = defineStore('faces', {
	state: () => ({
		faces: [],
		facesLoading: true,
		facesProgress: 0,
		lastUpdate: null,
		// the device scope (companies, offices) lastUpdate was reached for
		lastUpdateScope: null,
		audio: false,
		threshold: 50,
		confidence: 0.5,
		forceGeneration: false,
		faceDataVersion: null,
		serverReindexVersion: null,
		// sorted ids of the companies the last /reindex covered, joined by ','
		serverReindexCompanyIds: null,
		isLiveMode: false,
		flash: false,
		// server side recognition, deliberately not persisted: availability is a
		// property of right now, so a cold boot must re-probe instead of trusting
		// a stale value (see setServerRecognitionAvailable).
		serverRecognitionAvailable: null,
		serverRecognitionCooldownUntil: 0,
		serverRecognitionLastCheckAt: 0,
	}),
	persist: [
		{
			storage: 'native',
			pick: ['faces'],
			migrate: 'local',
			// compact format (base64 Float32Array per face); the deserializer
			// still reads the legacy blob, so existing kiosks need no regeneration
			serializer: { serialize: serializeFaces, deserialize: deserializeFaces },
		},
		{
			storage: 'native',
			pick: ['lastUpdate', 'lastUpdateScope', 'audio', 'threshold', 'confidence', 'forceGeneration', 'faceDataVersion', 'serverReindexVersion', 'serverReindexCompanyIds', 'isLiveMode', 'flash'],
			migrate: 'local',
		},
	],
	getters: {
		descriptors () {
			// Validate faces array exists and is valid
			if (!this.faces || !Array.isArray(this.faces)) {
				console.warn('[useFaceStore] descriptors getter: faces is not a valid array', this.faces);
				return [];
			}

			// Get current employee list for filtering at recognition time
			const { employees } = useEmployeeStore();
			const employeeIds = employees.value?.map(emp => emp.id) || [];

			// Filter out invalid faces and return only those with valid descriptors
			// AND that belong to currently active employees
			return this.faces
				?.filter(face => {
					if (!face || !face.id || !face.descriptors || !Array.isArray(face.descriptors) || face.descriptors.length === 0) {
						return false;
					}
					// Only use faces for employees that currently exist in the employee store
					return employeeIds.includes(face.id);
				})
				?.map(face => ({
					label: face.id,
					descriptors: face.descriptors,
				}));
		},
	},
	actions: {
		setFaces (faces) {
			this.faces = faces;
		},
		async addFace (face) {
			const entry = toFaceEntry(await formatFace(face));

			if (!entry) return;

			// the download and a socket event can both add the same employee
			// while the embedding is being computed: last one wins, no duplicates
			const index = this.faces?.findIndex(employee => employee.id === entry.id) ?? -1;

			if (index >= 0) {
				this.faces[index] = entry;
			} else {
				(this.faces ??= []).push(entry);
			}
		},
		removeFace (employeeId) {
			const index = this.faces?.findIndex(employee => employee.id === employeeId);

			if (index >= 0) {
				this.faces?.splice(index, 1);
			}
		},
		async updateFace (employeeId, update) {
			// only a new photo changes an entry; nothing else is stored on it, and
			// a no-op here is what keeps clock-ins from rewriting the whole blob
			if (!update?.createdFaceIds?.length) return;

			const entry = toFaceEntry(await formatFace({ ...update, id: employeeId }));

			// a photo with no detectable face keeps the previous descriptor
			if (!entry) return;

			// looked up after the await: the entry may have been removed meanwhile
			const index = this.faces?.findIndex(employee => employee.id === employeeId) ?? -1;

			if (index >= 0) {
				this.faces[index] = entry;
			}
		},
		hasFace (employeeId) {
			return this.faces?.findIndex(employee => employee.id === employeeId) >= 0;
		},
		setFacesLoading (bool) {
			this.facesLoading = bool;
		},
		setFacesProgress (progress) {
			this.facesProgress = progress;
		},
		setLastUpdate (date) {
			this.lastUpdate = date;
		},
		setLastUpdateScope (scope) {
			this.lastUpdateScope = scope;
		},
		setAudio (enable) {
			this.audio = enable;
		},
		setThreshold (threshold) {
			this.threshold = threshold;
		},
		setConfidence (confidence) {
			this.confidence = confidence;
		},
		setForceGeneration (enable) {
			this.forceGeneration = enable;
		},
		setFaceDataVersion (version) {
			this.faceDataVersion = version;
		},
		setServerReindexVersion (version) {
			this.serverReindexVersion = version;
		},
		setServerReindexCompanyIds (companyIds) {
			this.serverReindexCompanyIds = companyIds;
		},
		setIsLiveMode (enable) {
			this.isLiveMode = enable;
		},
		setFlash (enable) {
			this.flash = enable;
		},
		// null means unknown: callers treat it as "try the server", so a kiosk that
		// has not polled yet is never degraded.
		setServerRecognitionAvailable (available) {
			this.serverRecognitionAvailable = available;
		},
		setServerRecognitionCooldownUntil (timestamp) {
			this.serverRecognitionCooldownUntil = timestamp;
		},
		setServerRecognitionLastCheckAt (timestamp) {
			this.serverRecognitionLastCheckAt = timestamp;
		},
		// Local embeddings only: the server side collection has its own marker
		// (serverReindexVersion) so that neither rebuild drags the other along.
		checkFaceDataVersion (version) {
			if (this.faceDataVersion !== version) {
				this.setForceGeneration(true);
				this.setFaceDataVersion(version);
			}
		},
		reset () {
			this.setFaces(undefined);
			this.setLastUpdate(null);
			this.setLastUpdateScope(null);
			this.setAudio(false);
			this.setForceGeneration(false);
			this.setFaceDataVersion(null);
			this.setServerReindexVersion(null);
			this.setServerReindexCompanyIds(null);
			this.setIsLiveMode(false);
			this.setFlash(false);
			this.setServerRecognitionAvailable(null);
			this.setServerRecognitionCooldownUntil(0);
			this.setServerRecognitionLastCheckAt(0);
		},
	},
	sockets: {
		DocumentFaceIdCreated ({ employeeId, url }) {
			const data = { id: employeeId, createdFaceIds: [url] };

			this.hasFace(data?.id)
				? this.updateFace(data?.id, data)
				: this.addFace(data)
			;
		},
		DocumentFaceIdDeleted ({ employeeId }) {
			this.removeFace(employeeId);
		},
		CompanyDeviceDeleted () {
			this.reset();
		},
	},
});
